import { useMemo } from 'react';
import { t } from '@lingui/macro';
import { isEmpty } from 'lodash';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as businessServiceFilterId } from '../../filters/business-service-filter/BusinessServiceFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { businessServiceGroupFilterId, statusFilterId, timeRangeFilterId } from '../../filters';
import { useIsPgm } from '../../../stores/EnvironmentStore';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';

const title = t`Business Services`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'businessservice',
        metrics: ['status', 'bsPulseCounts', 'bsInfrastructureCounts'],
        entityFields: ['name', 'tags', 'status'],
        trends: ['status'],
        renderAsEntity: true,
    },
};

const sorters = [{ property: 'status', direction: 'desc' }];

function BusinessServiceListPanel(props) {
    const [isPgm] = useIsPgm();
    const columns = useMemo(
        () => [
            {
                type: COLUMN_TYPES.DRILLINLINK,
                title: t`Business Service`,
                sortable: true,
                mapping: 'businessservice.name',
                width: 250,
                link: {
                    target: '/business-service-detail',
                    mappedFilterParams: {
                        [businessServiceFilterId]: 'businessservice',
                    },
                    pageStateParams: ['SelectedTime'],
                },
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Tags`,
                sortable: false,
                mapping: 'businessservice.tags',
                formatter: (tags) => {
                    if (isEmpty(tags)) return DEFAULT_INVALID_VALUE;
                    return tags.join(', ');
                },
            },
            {
                type: COLUMN_TYPES.LINK,
                title: t`Local Manager`,
                sortable: true,
                removable: true,
                hidden: !isPgm,
                sortProperty: 'plm',
                mapping: 'plm',
                link: {
                    target: '/business-services',
                },
            },
            {
                title: t`Impacted nPoints`,
                width: 160,
                sortable: true,
                align: 'center',
                sortProperty: 'DIVIDE(bsPulseCounts.affected, bsPulseCounts.total)',
                mapper: (row) => `${row.bsPulseCounts?.affected} / ${row.bsPulseCounts?.total}`,
            },
            {
                title: t`Problem Infrastructure`,
                width: 200,
                sortable: true,
                align: 'center',
                sortProperty: 'DIVIDE(bsInfrastructureCounts.affected, bsInfrastructureCounts.total)',
                mapper: (row) => `${row.bsInfrastructureCounts?.affected} / ${row.bsInfrastructureCounts?.total}`,
            },
            {
                type: COLUMN_TYPES.STATUSTREND,
                title: t`Status Trend`,
                sortable: true,
                sortProperty: 'status',
                mapping: 'trends.status.data',
            },
        ],
        [isPgm]
    );

    const loader = useQueryLoader(dataSource);

    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="businessservice.deleted"
        />
    );
}

BusinessServiceListPanel.propTypes = panelPropTypes;

BusinessServiceListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'BusinessServicesList',
    catalogEntry: {
        category: PANEL_CATEGORIES.BUSINESS_SERVICE,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of business services, showing status trend, impacted nPoint counts, and problem infrastructure counts`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [statusFilterId, businessServiceGroupFilterId],
    },
})(BusinessServiceListPanel);
